import { PaletteTypeEnum, PaletteDetailModel } from '../models/palettes/palette_detail_model';
import { CategoryModel } from '../models/pattern/category-model';
import {
  ConfirmModalData,
  ConfirmModal,
} from '../modals/confirm-modal/confirm-modal';
import { MatDialog } from '@angular/material/dialog';
import { UnitOfMeasureEnum } from '../services/http-service';
import { StitchTypeEnum } from '../models/stitchs/stitch_model';
import { RealBeadImageModel } from '../models/palettes/real_bead_image_model';
import {
  NotificationComponent,
  NotificationTypeEnum,
} from '../components/notification/notification.component';
import { MatSnackBar } from '@angular/material/snack-bar';
import { PaletteModel } from '../models/palettes/palette_model';
import { PaletteRegistryService } from '../services/palette-registry.service';

type ConfirmCallback = (data: ConfirmModalData) => void;

export class Utility {
  static nonePatternsCategory = <CategoryModel>{
    categoryId: 0,
    userId: null,
    name: '',
    description: '',
    createdOn: null,
    lastUpdateOn: null,
  };

  static convertBeadsToSize(
    stitchTypeId: StitchTypeEnum,
    nBeads: number,
    beadType: BeadTypeEnum,
    beadMeasure: BeadMeasureEnum,
    unitOfMeasure: UnitOfMeasureEnum
  ): string {
    let beadSize;
    let beadUnitOfMeasure;

    const millimetersToCentimetersRatio = 0.1;
    const millimetersToInchRatio = 0.03937007874;

    switch (beadType) {
      case BeadTypeEnum.MiyukiDelica11:
        switch (stitchTypeId) {
          case StitchTypeEnum.Brick:
            beadSize = new BeadSize(1.7587, 1.35);
            break;

          case StitchTypeEnum.Peyote:
            beadSize = new BeadSize(1.35, 1.7587);
            break;

          case StitchTypeEnum.Loom:
            beadSize = new BeadSize(1.356, 1.75);
            break;
        }
        break;

      case BeadTypeEnum.PreciosaRocailles11:
        // Preciosa Rocailles 11/0 are round beads, approximately 2.1mm diameter
        switch (stitchTypeId) {
          case StitchTypeEnum.Brick:
            beadSize = new BeadSize(2.1, 1.8);
            break;

          case StitchTypeEnum.Peyote:
            beadSize = new BeadSize(1.8, 2.1);
            break;

          case StitchTypeEnum.Loom:
            beadSize = new BeadSize(2.1, 2.1);
            break;
        }
        break;
    }

    // Default to Miyuki Delica 11 if no bead size was set
    if (!beadSize) {
      switch (stitchTypeId) {
        case StitchTypeEnum.Brick:
          beadSize = new BeadSize(1.7587, 1.35);
          break;
        case StitchTypeEnum.Peyote:
          beadSize = new BeadSize(1.35, 1.7587);
          break;
        case StitchTypeEnum.Loom:
        default:
          beadSize = new BeadSize(1.356, 1.75);
          break;
      }
    }

    if (unitOfMeasure == UnitOfMeasureEnum.Cm) {
      beadUnitOfMeasure = 'cm';
    } else if (unitOfMeasure == UnitOfMeasureEnum.Mm) {
      beadUnitOfMeasure = 'mm';
    } else {
      beadUnitOfMeasure = 'in';
    }

    let ratio: number;

    switch (unitOfMeasure) {
      case UnitOfMeasureEnum.Cm:
        ratio = millimetersToCentimetersRatio;
        break;
      case UnitOfMeasureEnum.Mm:
        ratio = 1;
        break;

      case UnitOfMeasureEnum.Inch:
      default:
        ratio = millimetersToInchRatio;
        break;
    }

    if (beadMeasure == BeadMeasureEnum.Width)
      return (beadSize.width * nBeads * ratio).toFixed(2) + beadUnitOfMeasure;
    else
      return (beadSize.height * nBeads * ratio).toFixed(2) + beadUnitOfMeasure;
  }

  static getBeadClasses(paletteTypeId: number): string {
    let retVal = 'bead-image-palette ';

    switch (paletteTypeId) {
      case PaletteTypeEnum.Metallic:
        retVal += 'bead-metallic';
        break;

      case 3:
        retVal += 'bead-crystal';
        break;

      case 4:
        retVal += 'bead-transparent';
        break;

      case 5:
        retVal += 'bead-opaque';
        break;

      case 6:
        retVal += 'bead-matte';
        break;

      default:
        retVal += 'bead-matte';
        break;
    }

    return retVal;
  }

  static async delay(ms: number) {
    await new Promise<void>((resolve) => setTimeout(() => resolve(), ms)).then(
      () => console.log('fired')
    );
  }

  static confirmMessage(
    dialog: MatDialog,
    title: string,
    message: string,
    callback: ConfirmCallback
  ) {
    const dialogRef = dialog.open(ConfirmModal, {
      width: '500px',
      data: {
        title: title,
        message: message,
      },
    });
    dialogRef.afterClosed().subscribe(async (result) => {
      let data = <ConfirmModalData>result;
      if (data) {
        // console.log(data);
        callback(data);
      }
    });
  }

  static formatString(str: string, ...val: string[]) {
    for (let index = 0; index < val.length; index++) {
      str = str.replace(`{${index}}`, val[index]);
    }

    return str;
  }

  static getRealBeadPosition(name: string): number[] {
    for (
      let nRow = 0;
      nRow < RealBeadImageModel.realBeadImages.length;
      nRow++
    ) {
      var row = RealBeadImageModel.realBeadImages[nRow];

      for (let nCol = 0; nCol < row.length; nCol++) {
        var beadName = row[nCol];

        if (beadName == name) {
          return [
            nCol * (RealBeadImageModel.width + RealBeadImageModel.padding),
            nRow * (RealBeadImageModel.height + RealBeadImageModel.padding),
          ];
        }
      }
    }

    return null;
  }

  static hasTouchScreen(): boolean {
    return 'ontouchstart' in window || navigator.maxTouchPoints > 0;
  }

  static showNotification(
    snackBar: MatSnackBar,
    message: string,
    type: NotificationTypeEnum
  ): void {
    let className = '';

    switch (type) {
      case NotificationTypeEnum.Information:
        className = 'information-snackbar';
        break;
      case NotificationTypeEnum.Warning:
        className = 'warning-snackbar';
        break;
      case NotificationTypeEnum.Error:
        className = 'error-snackbar';
        break;
    }

    snackBar.openFromComponent(NotificationComponent, {
      duration: 5 * 1000,
      data: {
        message: message,
        type: type,
      },
      horizontalPosition: 'right',
      panelClass: [className],
    });
  }

  static traceLog(message: string): void {
    console.log(message);
    const stack = new Error().stack;
    const stackLines = stack.split('\n');
    stackLines.splice(0, 2);
    const last3Functions = stackLines.slice(0, 3).join('\n');
    console.log(last3Functions);
  }

  static getPalettesList(
    palettes: PaletteModel[],
    includeBuiltIn: boolean,
    includeCustom: boolean
  ): PaletteModel[] {
    return (
      palettes?.filter(
        (p) =>
          p.builtIn == includeBuiltIn ||
          (!p.builtIn && includeCustom)
      ) || []
    );
  }
}

class BeadSize {
  width: number;
  height: number;

  constructor(width: number, height: number) {
    this.width = width;
    this.height = height;
  }
}

export enum BeadTypeEnum {
  MiyukiDelica11,
  PreciosaRocailles11,
}

/**
 * Get the bead type for a palette ID using the registry.
 * Returns MiyukiDelica11 as default if palette is not mapped.
 */
export function getBeadTypeForPalette(
  paletteId: number,
  registry: PaletteRegistryService
): BeadTypeEnum {
  const miyukiId = registry.getMiyukiDelicaId();
  const preciosaId = registry.getPreciosaRocaillesId();

  if (paletteId === miyukiId) {
    return BeadTypeEnum.MiyukiDelica11;
  }
  if (paletteId === preciosaId) {
    return BeadTypeEnum.PreciosaRocailles11;
  }
  // Default to Miyuki Delica 11
  return BeadTypeEnum.MiyukiDelica11;
}

/**
 * Get the built-in palette ID for a bead.
 * Returns the originalPaletteId if available (for custom palettes),
 * otherwise returns paletteId if it's a known built-in palette.
 * Returns null if the bead has no built-in palette association.
 */
export function getBuiltInPaletteId(
  bead: PaletteDetailModel,
  registry: PaletteRegistryService
): number | null {
  if (!bead) return null;

  // If originalPaletteId is set, use it (this is for beads in custom palettes)
  if (bead.originalPaletteId && bead.originalPaletteId > 0) {
    return bead.originalPaletteId;
  }

  // If the bead's paletteId is a known built-in palette, return it
  if (registry.isBuiltInPaletteId(bead.paletteId)) {
    return bead.paletteId;
  }

  return null;
}

/**
 * Check if two beads are compatible (from the same built-in palette).
 * Compatible if either has no built-in palette association or they're from the same one.
 */
export function areBeadsCompatible(
  existingBead: PaletteDetailModel,
  newBead: PaletteDetailModel,
  registry: PaletteRegistryService
): boolean {
  const existingPalette = getBuiltInPaletteId(existingBead, registry);
  const newPalette = getBuiltInPaletteId(newBead, registry);

  // Compatible if either has no built-in palette or they're from the same one
  return !existingPalette || !newPalette || existingPalette === newPalette;
}

/**
 * Get a human-readable name for a built-in palette ID.
 */
export function getBuiltInPaletteName(
  paletteId: number,
  registry: PaletteRegistryService
): string {
  const paletteName = registry.getPaletteName(paletteId);
  return paletteName || 'Unknown';
}

export enum BeadMeasureEnum {
  Width,
  Height,
}

/**
 * Extract the bead type/palette info from pattern data JSON.
 * Returns the palette name. Defaults to "Miyuki Delica 11/0" if pattern has beads but type is unknown.
 *
 * Pattern data structure:
 * - paletteDetails: array of bead objects with full properties (paletteId, name, etc.)
 * - grid: 2D array of indices into paletteDetails (-1 = empty, 0+ = index into paletteDetails)
 */
export function extractPatternBeadTypeName(
  patternData: string,
  registry: PaletteRegistryService
): string | null {
  if (!patternData) {
    return null;
  }

  try {
    const data = JSON.parse(patternData);
    const paletteDetails = data?.paletteDetails;
    const grid = data?.grid;

    // First, try to detect from paletteDetails array (more reliable)
    if (paletteDetails && Array.isArray(paletteDetails)) {
      for (const bead of paletteDetails) {
        // Skip empty bead placeholder
        if (!bead || bead.paletteDetailId === -1) continue;

        // Try registry-based detection
        if (registry?.isInitialized()) {
          const builtInId = getBuiltInPaletteId(bead, registry);
          if (builtInId) {
            return getBuiltInPaletteName(builtInId, registry);
          }
        }

        // Fallback: detect from bead name patterns
        if (bead.name) {
          const beadType = detectBeadTypeFromName(bead.name);
          if (beadType) {
            return beadType;
          }
        }
      }
    }

    // Check if pattern has any beads (non-empty grid cells)
    let hasBeads = false;
    if (grid && Array.isArray(grid)) {
      for (const row of grid) {
        if (!Array.isArray(row)) continue;
        for (const cellValue of row) {
          // Grid contains indices: -1 = empty, 0+ = index into paletteDetails
          if (typeof cellValue === 'number' && cellValue >= 0) {
            hasBeads = true;
            break;
          }
          // Legacy format: grid contains bead objects directly
          if (typeof cellValue === 'object' && cellValue !== null) {
            const bead = cellValue as PaletteDetailModel;
            if (bead.paletteDetailId !== -1) {
              hasBeads = true;
              if (registry?.isInitialized()) {
                const builtInId = getBuiltInPaletteId(bead, registry);
                if (builtInId) {
                  return getBuiltInPaletteName(builtInId, registry);
                }
              }
              if (bead.name) {
                const beadType = detectBeadTypeFromName(bead.name);
                if (beadType) {
                  return beadType;
                }
              }
            }
          }
        }
        if (hasBeads) break;
      }
    }

    // Also check paletteDetails length (excluding empty placeholder at index 0)
    if (!hasBeads && paletteDetails && paletteDetails.length > 1) {
      hasBeads = true;
    }

    // Default to Miyuki Delica if pattern has beads but type couldn't be determined
    if (hasBeads) {
      return 'Miyuki Delica 11/0';
    }

    return null;
  } catch (e) {
    console.warn('Failed to parse pattern data for bead type extraction:', e);
    return null;
  }
}

/**
 * Detect bead type from bead name patterns.
 * Miyuki Delica 11/0 beads typically have names like "DB0001", "DB-0001", etc.
 * Preciosa Rocailles beads typically have 5-digit numeric codes like "02090", "22008".
 */
function detectBeadTypeFromName(name: string): string | null {
  if (!name) return null;

  const upperName = name.toUpperCase().trim();

  // Miyuki Delica 11/0 pattern: DB followed by digits (e.g., DB0001, DB-0001, DB 0001)
  // Check first as it's more specific (has letter prefix)
  if (/^DB[-\s]?\d{3,4}/.test(upperName)) {
    return 'Miyuki Delica 11/0';
  }

  // Preciosa Rocailles patterns:
  // 1. Explicit PR prefix or PRECIOSA in name
  if (/^PR[-\s]?\d/.test(upperName) || upperName.includes('PRECIOSA')) {
    return 'Preciosa Rocailles 11/0';
  }

  // 2. 5-digit numeric codes (e.g., "02090", "22008", "00050")
  //    Can be just the code or code followed by description (e.g., "02090 Opal White")
  //    Preciosa codes are typically 5-digit numbers, often starting with 0, 1, or 2
  if (/^\d{5}(\s|$)/.test(upperName)) {
    return 'Preciosa Rocailles 11/0';
  }

  return null;
}

/**
 * Extract pattern dimensions from pattern data JSON.
 * Returns an object with columns and rows, or null if not available.
 */
export function extractPatternDimensions(
  patternData: string
): { columns: number; rows: number } | null {
  if (!patternData) {
    return null;
  }

  try {
    const data = JSON.parse(patternData);
    const grid = data?.grid;

    if (!grid || !Array.isArray(grid) || grid.length === 0) {
      return null;
    }

    const rows = grid.length;
    const columns = Array.isArray(grid[0]) ? grid[0].length : 0;

    if (rows > 0 && columns > 0) {
      return { columns, rows };
    }

    return null;
  } catch (e) {
    return null;
  }
}

/**
 * Extract stitch type ID from pattern data JSON.
 * Returns the stitch ID or null if not available.
 */
export function extractPatternStitchId(patternData: string): number | null {
  if (!patternData) {
    return null;
  }

  try {
    const data = JSON.parse(patternData);
    return data?.stitchId ?? null;
  } catch (e) {
    return null;
  }
}
